import type { CalendarConnection, ConnectedCalendar } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useConnections,
  useDeleteConnection,
  useResyncConnection,
  useUpdateConnection,
} from '@/features/booking/api';
import { OfferVideoLocation } from '@/features/booking/offer-video-location';
import { btnSecondary, btnSmall } from '@/lib/ui';

interface CalendarsSearch {
  connected?: string;
  error?: string;
}

export const Route = createFileRoute('/app/booking/calendars')({
  validateSearch: (search: Record<string, unknown>): CalendarsSearch => ({
    ...(typeof search.connected === 'string' ? { connected: search.connected } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: CalendarsPage,
});

/**
 * Public subscription feeds (holidays, sports, …) are read-only and reject
 * Google's free/busy query, so they can't be used for conflict checks.
 * Holidays are handled by the availability schedule instead.
 */
function isPublicFeed(cal: ConnectedCalendar): boolean {
  return (
    !cal.canWrite && /#(holiday|contacts)@|@group\.v\.calendar\.google\.com$/.test(cal.externalId)
  );
}

function CalendarsPage() {
  const { connected, error } = Route.useSearch();
  const { t, i18n } = useTranslation(['booking', 'auth', 'common', 'errors']);
  const { data: connections = [], isLoading } = useConnections();
  const hasWriteTarget = connections.some(
    (c) => c.writeCalendarExternalId && c.status === 'active',
  );
  const errorKey =
    error && ['state_invalid', 'provider_error', 'calendar_scope_denied'].includes(error)
      ? `booking:calendars.errors.${error}`
      : error
        ? `errors:${error}`
        : null;

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-neutral-600">{t('booking:calendars.help')}</p>

      {connected ? (
        <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {t('booking:calendars.connected', { provider: t(`auth:providers.${connected}`) })}
        </p>
      ) : null}
      {errorKey ? (
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {t(errorKey)}
        </p>
      ) : null}
      {/*
        Sits right under the connect confirmation, because connecting is what
        makes a video link possible and this is where they land after doing it.
      */}
      <OfferVideoLocation />

      {!isLoading && connections.length > 0 && !hasWriteTarget ? (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t('booking:calendars.noWriteTarget')}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <a href="/api/v1/calendar/connect/google" className={btnSecondary}>
          {t('booking:calendars.connectGoogle')}
        </a>
        <a href="/api/v1/calendar/connect/microsoft" className={btnSecondary}>
          {t('booking:calendars.connectMicrosoft')}
        </a>
      </div>

      {isLoading ? <p className="text-sm text-neutral-500">{t('common:status.loading')}</p> : null}
      {!isLoading && connections.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">
          {t('booking:empty.noCalendar')}
        </p>
      ) : null}

      {connections.map((connection) => (
        <ConnectionCard key={connection.id} connection={connection} locale={i18n.language} />
      ))}
    </div>
  );
}

function ConnectionCard({
  connection,
  locale,
}: {
  connection: CalendarConnection;
  locale: string;
}) {
  const { t } = useTranslation(['booking', 'auth', 'common']);
  const update = useUpdateConnection();
  const resync = useResyncConnection();
  const remove = useDeleteConnection();
  const busy = update.isPending || resync.isPending || remove.isPending;

  const statusCls =
    connection.status === 'active'
      ? 'bg-green-100 text-green-800'
      : connection.status === 'needs_reauth'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-neutral-200 text-neutral-700';

  return (
    <section className="rounded-lg border border-neutral-200 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
        <div>
          <div className="text-sm font-medium">
            {t(`auth:providers.${connection.provider}`)} · {connection.email}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
            <span className={`rounded-full px-2 py-0.5 font-medium ${statusCls}`}>
              {t(`booking:calendars.status.${connection.status}`)}
            </span>
            {connection.lastSyncAt ? (
              <span>
                {t('booking:calendars.lastSync', {
                  when: new Intl.DateTimeFormat(locale, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(connection.lastSyncAt)),
                })}
              </span>
            ) : null}
            {connection.lastError ? (
              <span className="text-amber-700">{connection.lastError}</span>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2">
          {connection.status === 'needs_reauth' ? (
            <a href={`/api/v1/calendar/connect/${connection.provider}`} className={btnSmall}>
              {t('booking:calendars.reconnect')}
            </a>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => resync.mutate(connection.id)}
              className={btnSmall}
            >
              {t('booking:calendars.resync')}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (window.confirm(t('booking:calendars.disconnectConfirm')))
                remove.mutate(connection.id);
            }}
            className={`${btnSmall} text-red-700`}
          >
            {t('booking:calendars.disconnect')}
          </button>
        </div>
      </header>
      <ul className="divide-y divide-neutral-100">
        {connection.calendars.map((cal) => {
          const isWriteTarget = connection.writeCalendarExternalId === cal.externalId;
          const publicFeed = isPublicFeed(cal);
          return (
            <li
              key={cal.externalId}
              className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm"
            >
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: cal.colour ?? '#a3a3a3' }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">
                {cal.name}
                {!cal.canWrite ? (
                  <span className="ml-2 text-xs text-neutral-400">
                    {t('booking:calendars.readOnly')}
                  </span>
                ) : null}
              </span>
              {publicFeed ? (
                <span className="text-xs text-neutral-500">
                  {t('booking:calendars.publicFeedHint')}{' '}
                  <Link to="/app/booking/availability" className="underline">
                    {t('booking:nav.availability')}
                  </Link>
                </span>
              ) : (
                <label className="flex min-h-11 items-center gap-1.5 text-xs text-neutral-600 sm:min-h-0">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={cal.checkConflicts}
                    disabled={busy}
                    onChange={(e) =>
                      update.mutate({
                        id: connection.id,
                        calendars: [
                          { externalId: cal.externalId, checkConflicts: e.target.checked },
                        ],
                      })
                    }
                  />
                  {t('booking:calendars.checkConflicts')}
                </label>
              )}
              {isWriteTarget ? (
                <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-xs font-medium text-white">
                  {t('booking:calendars.writing')}
                </span>
              ) : cal.canWrite ? (
                <button
                  type="button"
                  disabled={busy || connection.status !== 'active'}
                  onClick={() =>
                    update.mutate({ id: connection.id, writeCalendarExternalId: cal.externalId })
                  }
                  className="flex min-h-11 items-center text-xs font-medium underline disabled:opacity-40 sm:min-h-0"
                >
                  {t('booking:calendars.writeHere')}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
